// ============================================================================
// APPROVAL SERVICE
// ============================================================================
// Business-logic + API calls for the Approval Inbox.

import { get, post, del } from '../client/axiosClient';
import { approvalEndpoints } from '../endpoints/approval.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../types';
import type {
  Approval,
  ApprovalSearchRequest,
  ApprovalDecisionRequest,
  ApprovalRoutingRow,
  UpdateApprovalRoutingRequest,
  CreateApprovalRoutingOverrideRequest,
  ContributedApproval,
} from '../types/approval.types';
import { put } from '../client/axiosClient';

class ApprovalService {
  /** Paged list with filters. Transforms backend PagedResult → frontend PaginatedResponse. */
  async getAll(params?: ApprovalSearchRequest): Promise<PaginatedResponse<Approval>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Approval', params)
      : approvalEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};
    const items: Approval[] = (pagedResult.items ?? []).map((a: Approval) => ({
      ...a,
      // SyutGrid expects `id`; we duplicate it from approvalRequestId for that.
      id: a.approvalRequestId,
    }));

    return {
      success: raw.success,
      message: raw.message,
      data: items,
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Approval>> {
    const response = await get<ApiResponse<Approval>>(approvalEndpoints.getById(id));
    return response.data;
  }

  async approve(id: ID, body: ApprovalDecisionRequest): Promise<ApiResponse<Approval>> {
    const response = await post<ApiResponse<Approval>>(approvalEndpoints.approve(id), body);
    return response.data;
  }

  async reject(id: ID, body: ApprovalDecisionRequest): Promise<ApiResponse<Approval>> {
    const response = await post<ApiResponse<Approval>>(approvalEndpoints.reject(id), body);
    return response.data;
  }

  /** All approval-routing rows (which role approves which type). */
  async getRouting(): Promise<ApiResponse<ApprovalRoutingRow[]>> {
    const response = await get<ApiResponse<ApprovalRoutingRow[]>>(approvalEndpoints.routing());
    return response.data;
  }

  /** Repoints one routing row's approver role (senior only, server-enforced). */
  async updateRouting(id: ID, body: UpdateApprovalRoutingRequest): Promise<ApiResponse<ApprovalRoutingRow>> {
    const response = await put<ApiResponse<ApprovalRoutingRow>>(approvalEndpoints.updateRouting(id), body);
    return response.data;
  }

  /**
   * Adds a property-scoped routing override (senior only, server-enforced).
   * Rejected when the type has no global row to refine.
   */
  async createRoutingOverride(
    body: CreateApprovalRoutingOverrideRequest,
  ): Promise<ApiResponse<ApprovalRoutingRow>> {
    const response = await post<ApiResponse<ApprovalRoutingRow>>(
      approvalEndpoints.createRoutingOverride(), body);
    return response.data;
  }

  /**
   * Retires a property override so the type falls back to its global routing
   * (senior only, server-enforced). Global rows are refused server-side.
   */
  async retireRoutingOverride(id: ID, rowVersion: number): Promise<ApiResponse<{ id: string }>> {
    const response = await del<ApiResponse<{ id: string }>>(
      approvalEndpoints.retireRoutingOverride(id, rowVersion));
    return response.data;
  }

  /** Other modules' items awaiting the caller (the restaurant's requisitions). */
  async getContributed(): Promise<ApiResponse<ContributedApproval[]>> {
    const response = await get<ApiResponse<ContributedApproval[]>>(approvalEndpoints.contributed());
    return response.data;
  }

  /** Approve or reject a contributed item; the owning module checks the caller holds the routed role. */
  async decideContributed(item: ContributedApproval, decision: 'approve' | 'reject', notes?: string): Promise<ApiResponse<unknown>> {
    const response = await post<ApiResponse<unknown>>(
      approvalEndpoints.decideContributed(item.source, item.itemId, decision), { notes, rowVersion: 0 });
    return response.data;
  }
}

export const approvalService = new ApprovalService();
export default approvalService;
